import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import SupportedSiteTag from './common/SupportedSiteTag';
import OperatingSystemTag from './common/OperatingSystemTag';
import CheckBox from './common/Checkbox';
import Button from './common/Button';
import { formatCentsAsUSD } from '../../lib/currency';
import currency from 'currency.js';
import { CONFIRM_ORDER, ME } from '../../lib/graphql/queries';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { START_ORDER } from '../../lib/graphql/mutations';
import {
  WHITE_LABEL_ACCENT_COLOR,
  WHITE_LABEL_STRIPE_PROMISES,
} from '../../lib/white-label';
import cookies from 'next-cookies';
import Loading from '../Loading';
// import useAuthOrRedirect from "../../hooks/useAuthOrRedirect";
import { useRouter } from 'next/router';
import momentTimezone from 'moment-timezone';
import QuestionMarkCircleIcon from '@heroicons/react/solid/QuestionMarkCircleIcon';
import dayjs from 'dayjs';
import { Transition } from '@tailwindui/react';
import { analytics } from '../../lib/analytics';
import { getReadableDiff } from '../../utils/getReadableDiff';
import useInterval from '../../hooks/useInterval';

const ConfirmModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  padding: 0 16px;
`;

const ConfirmModalCard = styled.div`
  width: 100%;
  max-width: 518px;
  background-color: #ffffff;
  border-radius: 14px;
  padding: 23px 27px;

  .left {
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: #9da3b7;
  }
  .right {
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: #000000;
    text-align: right;

    .edit {
      font-weight: 600;
      font-size: 12px;
      line-height: 15px;
      color: #9da3b7;
      text-align: right;
      .link {
        font-weight: 600;
        font-size: 12px;
        line-height: 15px;
        color: ${(props) => WHITE_LABEL_ACCENT_COLOR};
        text-align: right;
        margin-left: 2px;
        cursor: pointer;
      }
    }
  }

  & > .title {
    font-size: 24px;
    line-height: 29px;
    color: #05030d;
    font-weight: bold;
  }
`;

const ConfirmModalCardHead = styled.div`
  display: grid;
  grid-template-columns: 114px 1fr;
  grid-gap: 0 19px;
  margin-top: 28px;
  border-bottom: 1px solid #e5e8f0;
  padding-bottom: 24px;
  margin-bottom: 19px;
`;

const ConfirmModalCardHeadImage = styled.div`
  position: relative;
  height: 85px;
  width: 85px;
  div {
    border-radius: 10px;
  }
`;

const ConfirmModalCardHeadBody = styled.div`
  & > .row {
    display: flex;
    grid-gap: 7px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .product-name {
    font-weight: bold;
    font-size: 18px;
    line-height: 22px;
    color: #05030d;
  }
  .supported-retailers {
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    color: #9da3b7;
    margin-bottom: 4px;
  }
`;

const ConfirmModalDetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 21px;
`;

const ConfirmModalCardFee = styled.div`
  background: #f2f4f9;
  border: 1px solid #e5e8f0;
  border-radius: 9px;
  padding: 17px 19px;
`;

const ConfirmModalBalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ConfirmModalTotalDueRow = styled.div`
  border-top: 1px dashed #dbe0ec;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 19px;
  .right {
    font-weight: 600;
    font-size: 18px;
    line-height: 17px;
    color: #000000;
    text-align: right;
  }
`;

const ConfirmModalFooter = styled.div`
  margin-top: 16px;
  .button-group {
    display: grid;
    grid-template-columns: 125px 1fr;
    grid-gap: 0 15px;
    margin-top: 19px;
    align-items: center;
  }
`;

const ConfirmModal = ({
  startDate,
  endDate,
  rentalDays,
  listingPrice,
  discountPrice,
  totalPrice,
  listingId,
  bot,
  onClose,
  affiliateId,
  onError,
  setTourOpen,
  blockScroll,
}: {
  startDate;
  endDate;
  rentalDays;
  listingId;
  bot;
  onClose;
  affiliateId;
  onError;
  setTourOpen;
  blockScroll;
  listingPrice: number;
  totalPrice: number;
  discountPrice?: number;
}): JSX.Element => {
  const [tags, setTags] = useState(bot.tags.split(','));
  const [os, setOs] = useState(bot.operatingSystem.split(','));
  const [isTosChecked, setIsTosChecked] = useState(false);
  const [isTimeChecked, setIsTimeChecked] = useState(false);
  const [shouldUseBalance, setShouldUseBalance] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [listingIdState, setListingIdState] = useState(listingId);
  useEffect(() => {
    setListingIdState(listingIdState);
  }, [listingId]);
  const [noFeeChecked, setNoFeeChecked] = useState(false);
  const priceDisplay = useMemo(
    () => formatCentsAsUSD(totalPrice),
    [totalPrice],
  );
  const pricePerDay = useMemo(() => {
    return discountPrice || listingPrice;
  }, [discountPrice, listingPrice]);
  const fee = useMemo(() => {
    return totalPrice * 0.1;
  }, [totalPrice]);
  const feeDisplay = useMemo(() => {
    if (noFeeChecked) {
      return formatCentsAsUSD(0);
    }
    return formatCentsAsUSD(fee);
  }, [fee, noFeeChecked]);

  const { loading, data, error } = useQuery(CONFIRM_ORDER, {
    variables: {
      id: Number(listingIdState),
    },
  });

  const [startOrder, startOrderResult] = useMutation(START_ORDER);
  const router = useRouter();

  const onConfirm = () => {
    const utcStartDate = startDate;
    const utcEndDate = endDate;
    function getClientReferenceId() {
      return (
        // @ts-ignore
        (window.Rewardful && window.Rewardful.referral) ||
        'checkout_' + new Date().getTime()
      );
    }
    const clientReferenceId = getClientReferenceId();
    if (
      confirm(
        'Alert: You will have 5 minutes to complete your order. Please confirm you are in fewer than 100 discord servers before purchasing.',
      )
    ) {
      analytics.track('Confirmed Rental', {
        isNoFeePurchase: noFeeChecked,
        startDate: utcStartDate,
        endDate: utcEndDate,
        listingId: Number(listingId),
        affiliateId: affiliateId,
        clientReferenceId,
        shouldUseBalance,
        botId: bot.id,
      });
      setIsLoading(true);
      startOrder({
        variables: {
          startOrderData: {
            isNoFeePurchase: noFeeChecked,
            startDate: utcStartDate,
            endDate: utcEndDate,
            listingId: Number(listingId),
            affiliateId: affiliateId,
            clientReferenceId,
            shouldUseBalance,
          },
        },
      })
        .then(async (res) => {
          if (res.data?.startOrder?.includes('credit-')) {
            // this is a full credit order with no requirement of stripe
            analytics.track('Credit Order Accepted');
            return router.push('/bots/completed-order');
          }

          const stripe = await WHITE_LABEL_STRIPE_PROMISES[
            data.listing.whiteLabelOrg
          ];
          analytics.track('Sent to Stripe Checkout', {
            sessionId: res.data.startOrder,
          });
          stripe.redirectToCheckout({
            sessionId: res.data.startOrder,
          });
        })
        .catch((err) => {
          // console.log(err.message);
          if (err.message == 'MUST_BE_AUTHENTICATED') {
            analytics.track('Login to Start Order', {
              startDate: utcStartDate,
              endDate: utcEndDate,
              listingId: Number(listingId),
            });
            router.push('/login');
            alert('Make sure you are logged in!');
          } else if (err.message == 'MUST_AUTHENTICATE_DISCORD') {
            analytics.track('Requires Discord to Start Order', {
              startDate: utcStartDate,
              endDate: utcEndDate,
              listingId: Number(listingId),
            });
            router.push('/register/connect-discord');
            alert('Connect discord to purchase');
          } else if (err.message) {
            analytics.track('Failed to Start Order', {
              startDate: utcStartDate,
              endDate: utcEndDate,
              listingId: Number(listingId),
              errorMessage: err.message,
            });
            onError()
              .then(() => {
                alert('Please select new dates for your order!');
              })
              .finally(() => {
                onClose();
              });
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const me = data?.me;

  const totalData = useMemo(() => {
    const originalAmount = totalPrice + (noFeeChecked ? 0 : fee);
    let amount = originalAmount;
    let balance = me?.creditBalance;
    let balanceUsed = 0;

    if (shouldUseBalance) {
      if (balance > amount) {
        balance = balance - amount;
        amount = 0;
        balanceUsed = me?.creditBalance - balance;
      } else {
        balanceUsed = balance;
        amount = amount - balance;
        balance = 0;
      }
    }

    const originalTotalDisplay = currency(originalAmount, {
      fromCents: true,
    }).format();

    const totalDisplay = currency(amount, {
      fromCents: true,
    }).format();

    const balanceDisplay = currency(balance, {
      fromCents: true,
    }).format();

    const balanceUsedDisplay = currency(balanceUsed, {
      fromCents: true,
    }).format();

    return {
      balanceDisplay,
      balanceUsedDisplay,
      totalDisplay,
      originalTotalDisplay,
    };
  }, [listingPrice, me, shouldUseBalance, noFeeChecked]);

  const [timer, setTimer] = useState(0);

  useInterval(() => {
    setTimer((prior) => prior + 1);
  }, 60000);

  const startDiff = useMemo(
    () => getReadableDiff(dayjs(startDate)),
    [startDate, timer],
  );

  if (loading)
    return (
      <ConfirmModalWrapper>
        <Loading />
      </ConfirmModalWrapper>
    );
  // if (!useAuthOrRedirect()) return null;
  if (!listingId) {
    router.push('/bots');
    return null;
  }

  return (
    <ConfirmModalWrapper>
      <ConfirmModalCard>
        <div className="flex title" data-tour="step-8">
          <p className="flex-col">Confirm your rental</p>
          <QuestionMarkCircleIcon
            onClick={() => {
              setTourOpen(true);
              blockScroll();
            }}
            className="mr-1 h-8 w-8 text-gray-400 bg-gray-100 p-2 rounded-lg flex-col ml-4"
          />
        </div>
        <ConfirmModalCardHead data-tour="step-9">
          <ConfirmModalCardHeadImage>
            <Image alt="" layout="fill" src={bot.imageUrl} />
          </ConfirmModalCardHeadImage>
          <ConfirmModalCardHeadBody>
            <div className="row">
              <p className="product-name">{bot.name}</p>
              {os.map((o, index) => (
                <OperatingSystemTag key={index} text={o} />
              ))}
            </div>
            <p className="supported-retailers">Supported Retailers</p>
            <div className="row">
              {tags.map((tag, index) => (
                <SupportedSiteTag key={index} text={tag} />
              ))}
            </div>
          </ConfirmModalCardHeadBody>
        </ConfirmModalCardHead>
        <ConfirmModalDetailRow data-tour="step-10">
          <p className="left">Start Date</p>
          <p className="right">
            {startDate.format('L') + ' at ' + startDate.format('LT')}
          </p>
        </ConfirmModalDetailRow>
        <ConfirmModalDetailRow data-tour="step-11">
          <p className="left">End Date</p>
          <div className="right">
            <p>{endDate.format('L') + ' at ' + endDate.format('LT')}</p>
            <p className="edit">
              Want to edit your date?
              <span className="link" onClick={() => onClose()}>
                Go back
              </span>
            </p>
          </div>
        </ConfirmModalDetailRow>
        <ConfirmModalDetailRow>
          <p className="left">Rental period</p>
          <p className="right">{rentalDays} days</p>
        </ConfirmModalDetailRow>
        <ConfirmModalCardFee data-tour="step-12">
          <ConfirmModalDetailRow>
            <p className="left">Rental price</p>
            <p className="right">
              <span>{priceDisplay}</span>
              {typeof data?.me?.creditBalance === 'number' &&
                data?.me?.creditBalance > 0 && (
                  <p className="edit mt-1">
                    <a
                      className="link"
                      onClick={() => {
                        setShouldUseBalance(shouldUseBalance ? false : true);
                      }}
                    >
                      {!shouldUseBalance ? (
                        <>
                          {' '}
                          Use available credit{' '}
                          {data?.me?.creditBalance
                            ? `$${(data?.me?.creditBalance / 100).toFixed(2)}`
                            : '0.00'}
                        </>
                      ) : (
                        <>Don't use available credit </>
                      )}
                    </a>
                  </p>
                )}
            </p>
          </ConfirmModalDetailRow>
          <ConfirmModalDetailRow>
            <p className="left">Fee</p>
            <div className="right">
              <p>{feeDisplay}</p>
              {me?.purchasesUntilNoFee === 0 && (
                <p className="edit">
                  <button
                    className="link"
                    onClick={() => {
                      setNoFeeChecked(!noFeeChecked);
                    }}
                  >
                    {noFeeChecked
                      ? 'Purchase (With Fees)'
                      : 'Purchase (Zero Fees)'}
                  </button>
                </p>
              )}
            </div>
          </ConfirmModalDetailRow>
          <ConfirmModalBalanceRow className={shouldUseBalance ? '' : 'mb-2'}>
            <p className="left">Total</p>
            <div className="right">
              <p>{totalData.totalDisplay}</p>
            </div>
          </ConfirmModalBalanceRow>
          <Transition
            show={shouldUseBalance}
            enter="transition-opacity duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-250"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ConfirmModalBalanceRow className="my-3">
              <p className="left">Credit Used</p>
              <p className="right">- {totalData.balanceUsedDisplay}</p>
            </ConfirmModalBalanceRow>
            <ConfirmModalBalanceRow className="mt-3 mb-2">
              <p className="left">Remaining Credit</p>
              <p className="right">{totalData.balanceDisplay}</p>
            </ConfirmModalBalanceRow>
          </Transition>
          <ConfirmModalTotalDueRow>
            <p className="left">Amount due</p>
            <p className="right">
              <p>{totalData.totalDisplay}</p>
            </p>
          </ConfirmModalTotalDueRow>
        </ConfirmModalCardFee>
        <ConfirmModalFooter>
          <CheckBox
            dataTour="step-14"
            text="I accept the"
            specialText="terms and conditions"
            checked={isTosChecked}
            onChanged={(e) => {
              setIsTosChecked(e);
              if (!startDiff) setIsTimeChecked(true);
            }}
            onSpecialClicked={(e) => {
              if (typeof window !== 'undefined') {
                window?.open('/info/terms', '_blank');
              }
            }}
          />
          {startDiff && (
            <div className="mt-1">
              <CheckBox
                text={`I understand the rental begins in ${startDiff}`}
                specialText=""
                onChanged={(e) => {
                  setIsTimeChecked(e);
                }}
              />
            </div>
          )}
          <div className="button-group">
            <Button
              onClick={() => {
                onClose();
              }}
              isLoading={false}
              backgroundColor={' #F2F4F9'}
              color="#9DA3B7"
              isDisabled={false}
              text="Cancel"
            />
            <Button
              onClick={() => {
                onConfirm();
              }}
              isLoading={isLoading}
              backgroundColor={
                isTosChecked && isTimeChecked
                  ? WHITE_LABEL_ACCENT_COLOR
                  : `${WHITE_LABEL_ACCENT_COLOR}88`
              }
              isDisabled={!isTosChecked || !isTimeChecked}
              color="#ffffff"
              text="Continue to checkout"
              dataTour="step-15"
            />
          </div>
        </ConfirmModalFooter>
      </ConfirmModalCard>
      <ConfirmModalCard id="checkout" className="hidden"></ConfirmModalCard>
    </ConfirmModalWrapper>
  );
};

export default ConfirmModal;

ConfirmModal.defaultProps = {
  affliateId: '',
};
